// frontend/src/features/wind/WindFarmsApp.js
import React, { useEffect, useMemo, useState } from "react";
import api from "../../apiClient";
import WindFiltersPanel from "./WindFiltersPanel";
import WindMapPanel from "./WindMapPanel";
import WindProjectListPanel from "./WindProjectListPanel";
import CompSetPanel from "./CompSetPanel";
import { InfoChip } from "./ui";

/**
 * WindFarmsApp
 *
 * Container: handles data loading, global state, and wires subcomponents.
 */
export default function WindFarmsApp({ email, onClose, logout }) {
  // --------- Filters (UI) ----------
  const [region, setRegion] = useState("TX"); // TX | OK | US
  const [capacityMin, setCapacityMin] = useState("50");
  const [capacityMax, setCapacityMax] = useState("");
  const [yearMin, setYearMin] = useState("2000");
  const [yearMax, setYearMax] = useState("");
  const [applyNonce, setApplyNonce] = useState(0); // refetch trigger

  // --------- Data from backend ----------
  const [summary, setSummary] = useState(null);
  const [projectsRaw, setProjectsRaw] = useState([]);
  const [loading, setLoading] = useState(false); // start idle, not loading
  const [error, setError] = useState("");

  // --------- Selection + Comp Set (in memory) ----------
  const [selectedIds, setSelectedIds] = useState([]); // project.id[]
  const [selectedProject, setSelectedProject] = useState(null); // subject when not in comp mode
  const [compMode, setCompMode] = useState(false);
  const [subjectId, setSubjectId] = useState(null);
  const [compValues, setCompValues] = useState({}); // { [id]: { assessed: string } }

  // --------- Map fullscreen ----------
  const [mapFullscreen, setMapFullscreen] = useState(false);

  // Map region to backend param
  const stateParam = useMemo(
    () => (region === "US" ? undefined : region || undefined),
    [region]
  );

  const minCapParam = useMemo(() => {
    const n = parseFloat(capacityMin);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [capacityMin]);

  const capacityMaxNum = useMemo(() => {
    const n = parseFloat(capacityMax);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [capacityMax]);

  const yearMaxNum = useMemo(() => {
    const n = parseInt(yearMax, 10);
    return Number.isFinite(n) ? n : null;
  }, [yearMax]);

  // --------- Load projects from /wind/projects ----------
  useEffect(() => {
    // Do nothing until the user has clicked "Apply filters" at least once
    if (applyNonce === 0) {
      return;
    }

    let cancelled = false;

    async function loadProjects() {
      setLoading(true);
      setError("");

      try {
        // Clamp min year so we never violate backend's ge>=1900 constraint
        const rawMinYear = parseInt(yearMin, 10);
        const safeMinYear =
          Number.isFinite(rawMinYear) && rawMinYear >= 1900
            ? rawMinYear
            : 1900;

        const res = await api.get("/wind/projects", {
          params: {
            state: stateParam,
            min_capacity_mw: minCapParam,
            min_year: safeMinYear,
          },
        });

        if (cancelled) return;
        const data = res.data || {};
        const s = data.summary || null;
        const p = Array.isArray(data.projects) ? data.projects : [];
        setSummary(s);
        setProjectsRaw(p);

        // Reset selection / comp set when filters change
        setSelectedIds([]);
        setSelectedProject(p.length ? p[0] : null);
        setCompMode(false);
        setSubjectId(null);
        setCompValues({});
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail ||
              "Unable to load wind farm projects from backend."
          );
          setSummary(null);
          setProjectsRaw([]);
          setSelectedIds([]);
          setSelectedProject(null);
          setCompMode(false);
          setSubjectId(null);
          setCompValues({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [applyNonce, stateParam, minCapParam, yearMin]);

  // Apply capacity & vintage windows client-side (max bounds)
  const projectsWindowed = useMemo(() => {
    return projectsRaw.filter((p) => {
      const cap =
        typeof p.capacity_mw === "number" ? Number(p.capacity_mw) : null;
      const yr =
        typeof p.year === "number" || typeof p.year === "string"
          ? parseInt(p.year, 10)
          : null;

      if (capacityMaxNum != null && cap != null && cap > capacityMaxNum) {
        return false;
      }
      if (yearMaxNum != null && yr != null && yr > yearMaxNum) {
        return false;
      }
      return true;
    });
  }, [projectsRaw, capacityMaxNum, yearMaxNum]);

  const regionLabel =
    summary?.region_label ||
    (region === "US" ? "US (all states)" : region || "Region");

  const projectCount = projectsWindowed.length;
  const totalCapacity = projectsWindowed.reduce((acc, p) => {
    const val =
      typeof p.capacity_mw === "number" ? Number(p.capacity_mw) : 0;
    return acc + val;
  }, 0);

  const regionLabelShort =
    region === "US" ? "US" : region || summary?.region_label || "Region";

  // --------- Selection & Comp Set helpers ----------
  const handleToggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setCompMode(false);
    setSubjectId(null);
    setCompValues({});
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setCompMode(false);
    setSubjectId(null);
    setCompValues({});
  };

  const selectedProjects = useMemo(
    () => projectsWindowed.filter((p) => selectedIds.includes(p.id)),
    [projectsWindowed, selectedIds]
  );

  const handleStartCompSet = () => {
    if (!selectedProjects.length) return;
    setCompMode(true);

    const defaultSubject =
      selectedProjects.find((p) => p.id === subjectId)?.id ||
      selectedProjects[0].id;
    setSubjectId(defaultSubject);

    const newValues = {};
    selectedProjects.forEach((p) => {
      newValues[p.id] = compValues[p.id] || { assessed: "" };
    });
    setCompValues(newValues);
  };

  const handleUpdateAssessedValue = (projectId, value) => {
    setCompValues((prev) => ({
      ...prev,
      [projectId]: { ...(prev[projectId] || {}), assessed: value },
    }));
  };

  const handleMapMarkerClick = (projectId, isShift) => {
    const proj =
      projectsWindowed.find((p) => p.id === projectId) ||
      projectsRaw.find((p) => p.id === projectId);
    if (!proj) return;

    if (isShift) {
      handleToggleSelected(projectId);
    } else {
      if (!compMode) {
        setSelectedProject(proj);
      }
    }
  };

  const canStartCompSet = selectedIds.length >= 2;

  const handleToggleMapFullscreen = () => {
    setMapFullscreen((prev) => !prev);
  };

  // --------- Render ----------
  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-400 via-emerald-400 to-blue-500 text-slate-950 grid place-items-center shadow-lg">
              <span className="text-xs font-black tracking-[0.12em] uppercase">
                WF
              </span>
            </div>
            <div className="leading-tight">
              <div className="text-lg font-semibold tracking-tight">
                Wind Farms
              </div>
              <div className="text-xs text-slate-400">
                Global wind farm intelligence &amp; AV/MW benchmarking
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {email && (
              <div className="hidden sm:block text-xs text-slate-400">
                Signed in as{" "}
                <span className="font-medium text-slate-200">{email}</span>
              </div>
            )}
            <button
              onClick={onClose}
              className="hidden sm:inline-flex items-center gap-1 rounded-xl border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800/80 transition"
            >
              <span>←</span>
              <span>Back to Apps</span>
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1 rounded-xl border border-rose-400/60 text-rose-200 px-3 py-1.5 text-xs bg-rose-900/20 hover:bg-rose-900/40 transition"
              title="Log out"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Fullscreen map overlay */}
      {mapFullscreen && (
        <div className="fixed inset-0 z-40 bg-slate-950/95">
          <div className="absolute inset-4">
            <WindMapPanel
              projects={projectsWindowed}
              selectedIds={selectedIds}
              subjectId={subjectId}
              regionLabelShort={regionLabelShort}
              onMarkerClick={handleMapMarkerClick}
              onToggleFullscreen={handleToggleMapFullscreen}
              isFullscreen
            />
          </div>
        </div>
      )}

      {/* Main layout */}
      <main className="flex-1">
        <div className="max-w-[1800px] mx-auto px-6 py-8 space-y-6">
          {/* Top summary strip */}
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <InfoChip
              label="Region"
              value={regionLabel}
              detail="Use filters below to change state, capacity, and vintage windows."
            />
            <InfoChip
              label="Wind farms in view"
              value={
                loading
                  ? "Loading…"
                  : projectCount
                  ? projectCount.toLocaleString()
                  : "0"
              }
              detail="Count of USWTDB projects that match your current window."
            />
            <InfoChip
              label="Total capacity (MW)"
              value={
                loading
                  ? "Loading…"
                  : totalCapacity
                  ? totalCapacity.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })
                  : "0"
              }
              detail="Sum of project capacities in the current view."
            />
          </section>

          {error && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-900/30 px-4 py-3 text-[11px] text-amber-100">
              {error}
            </div>
          )}

          {/* Main 2-column layout */}
          <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)] gap-4 lg:gap-6">
            {/* Left: Filters + Map + Project list */}
            <div className="space-y-4 lg:space-y-5">
              <WindFiltersPanel
                region={region}
                capacityMin={capacityMin}
                capacityMax={capacityMax}
                yearMin={yearMin}
                yearMax={yearMax}
                onRegionChange={setRegion}
                onCapacityMinChange={setCapacityMin}
                onCapacityMaxChange={setCapacityMax}
                onYearMinChange={setYearMin}
                onYearMaxChange={setYearMax}
                onApply={() => setApplyNonce((n) => n + 1)}
                loading={loading}
              />

              <WindMapPanel
                projects={projectsWindowed}
                selectedIds={selectedIds}
                subjectId={subjectId}
                regionLabelShort={regionLabelShort}
                onMarkerClick={handleMapMarkerClick}
                onToggleFullscreen={handleToggleMapFullscreen}
                isFullscreen={false}
              />

              <WindProjectListPanel
                projects={projectsWindowed}
                loading={loading}
                selectedIds={selectedIds}
                selectedProject={selectedProject}
                onToggleSelected={handleToggleSelected}
                onClearSelection={handleClearSelection}
                onProjectClick={(p) => !compMode && setSelectedProject(p)}
                onStartCompSet={handleStartCompSet}
                canStartCompSet={canStartCompSet}
              />
            </div>

            {/* Right: Workspace / Comp Set editor */}
            <div className="space-y-4 lg:space-y-5">
              <CompSetPanel
                compMode={compMode}
                selectedProject={selectedProject}
                selectedProjects={selectedProjects}
                subjectId={subjectId}
                onSubjectIdChange={setSubjectId}
                compValues={compValues}
                onUpdateAssessedValue={handleUpdateAssessedValue}
              />
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
